import type { BridgeDeck } from '../../site/types';
import type { MeshBuilder, VertexStyle, Frame } from '../mesh/meshBuilder';
import type { Vec2 } from '../../geo/types';
import { buildRailing } from './bridgeRailing';
import { layoutToWorld } from '../../core/coords';
import { bridgeProfile } from '../../site/bridgeHeight';
import { buildArchBridge, type ArchBridgeSpec } from './archBridge';
import { buildArchDressings } from './archDressings';
import { hexahedronSolid } from '../fabric/solids';
import { buildNieuweBrugTimber } from './nieuweBrugTimber';

/** The movable timber opening divides the painted masonry approaches, rather than closing it. */
export function nieuweBrugTimberSpan(bridge: BridgeDeck): readonly [number, number] {
  const p = bridgeProfile(bridge),
    length = p.to - p.from;
  return [
    p.from + length * 0.64,
    Math.min(p.from + length * 0.88, p.knots[p.knots.length - 2] ?? p.to),
  ];
}

/** Two stone approach arcades and the central timber span. Vertical dimensions are estimates. */
export function buildNieuweBrug(
  builder: MeshBuilder,
  stone: VertexStyle,
  wood: VertexStyle,
  bridge: BridgeDeck
): void {
  const p = bridgeProfile(bridge);
  const [start, end] = nieuweBrugTimberSpan(bridge);
  const originAt = (x: number) =>
    layoutToWorld([bridge.a[0] + p.axis[0] * x, bridge.a[1] + p.axis[1] * x], 0);
  const yaw = Math.atan2(p.axis[1], p.axis[0]);
  const approaches = [
    {
      from: p.from,
      length: start - p.from,
      arches: [0.85, 1, 1.1, 1, 0.85],
      bankMargin: 12 + Math.max(0, (bridge.westLandingEnd ?? p.from) - p.from),
      innerMargin: 2,
      left: true,
    },
    {
      from: end,
      length: p.to - end,
      arches: [1],
      bankMargin: 2,
      innerMargin: 2,
      left: false,
    },
  ];
  for (const a of approaches) {
    const pierWidth = 2.4;
    const available = a.length - a.bankMargin - a.innerMargin - (a.arches.length - 1) * pierWidth;
    const sum = a.arches.reduce((x, y) => x + y, 0);
    const frame = { origin: originAt(a.from), yaw };
    const spec: ArchBridgeSpec = {
      length: a.length,
      width: bridge.width,
      spans: a.arches.map((w) => (w * available) / sum),
      pierWidth,
      firstArchAt: a.left ? a.bankMargin : a.innerMargin,
      springY: -0.6,
      crownY: a.left ? 2.6 : 1.2,
      footY: -2.5,
      parapet: { height: 0, thickness: 0 },
      deckY: (x) => p.height(a.from + x),
    };
    buildArchBridge(builder, stone, frame, spec);
    buildArchDressings(builder, stone, frame, spec);
  }
  const frame = { origin: originAt(start), yaw },
    height = p.height(start);
  buildNieuweBrugTimber(builder, wood, frame, end - start, bridge.width, height);
}

const PARAPET = { height: 0.6, thickness: 0.3 };
function sideFrame(side: { a: Vec2; b: Vec2 }): Frame {
  return {
    origin: layoutToWorld(side.a, 0),
    yaw: Math.atan2(side.b[1] - side.a[1], side.b[0] - side.a[0]),
  };
}
/** Masonry copings follow the actual bank-clipped walkway, including the approach slopes. */
export function buildNieuweBrugParapets(
  builder: MeshBuilder,
  stone: VertexStyle,
  wood: VertexStyle,
  bridge: BridgeDeck,
  sides: readonly { a: Vec2; b: Vec2 }[]
): void {
  const profile = bridgeProfile(bridge);
  const timber = nieuweBrugTimberSpan(bridge);
  for (const side of sides) {
    const start = profile.along(side.a);
    const direction = profile.along(side.b) >= start ? 1 : -1;
    const length = Math.hypot(side.b[0] - side.a[0], side.b[1] - side.a[1]);
    const cuts = [
      0,
      ...[
        ...profile.knots,
        ...timber,
        ...(bridge.westLandingEnd === undefined
          ? []
          : [bridge.westLandingEnd + (bridge.westStairRun ?? 0)]),
      ]
        .map((x) => (x - start) * direction)
        .filter((x) => x > 1e-5 && x < length - 1e-5),
      length,
    ].sort((a, b) => a - b);
    for (let i = 1; i < cuts.length; i++) {
      const a = cuts[i - 1]!,
        b = cuts[i]!;
      if (b - a < 1e-5) continue;
      const ya = profile.height(start + direction * a),
        yb = profile.height(start + direction * b);
      const station = start + (direction * (a + b)) / 2;
      const middle: Vec2 = [(side.a[0] + side.b[0]) / 2, (side.a[1] + side.b[1]) / 2];
      const seaward =
        -(middle[0] - bridge.a[0]) * profile.axis[1] + (middle[1] - bridge.a[1]) * profile.axis[0] >
        0;
      // The masonry corner landing opens directly off the western head. A guard
      // across it would obstruct the route and cover the return stair's upper treads.
      if (
        seaward &&
        bridge.westLandingEnd !== undefined &&
        station < bridge.westLandingEnd + (bridge.westStairRun ?? 0)
      )
        continue;
      if (station > timber[0] && station < timber[1]) {
        const frame = sideFrame(side);
        buildRailing(
          builder,
          wood,
          {
            ...frame,
            origin: layoutToWorld(
              [
                side.a[0] + ((side.b[0] - side.a[0]) * a) / length,
                side.a[1] + ((side.b[1] - side.a[1]) * a) / length,
              ],
              0
            ),
          },
          b - a,
          (x) => profile.height(start + direction * (a + x))
        );
        continue;
      }
      hexahedronSolid(builder, stone, sideFrame(side), [
        [a, ya, 0],
        [a, ya + PARAPET.height, 0],
        [b, yb, 0],
        [b, yb + PARAPET.height, 0],
        [a, ya, PARAPET.thickness],
        [a, ya + PARAPET.height, PARAPET.thickness],
        [b, yb, PARAPET.thickness],
        [b, yb + PARAPET.height, PARAPET.thickness],
      ]);
    }
  }
}
